import { gridDisk, latLngToCell } from "h3-js";
//#region ../src/geo/h3-proximity.ts
/**
* H3 Geo-Anchor Proximity Matching
*
* Generic utility for matching GPS positions to known geo-anchored points
* using H3 hexagonal grid indices. Uses gridDisk(cell, 1) to create a ~65m
* safe zone that absorbs GPS jitter of 3-10m.
*
* Renamed from `ref-points/h3-ref-point.ts` in Iter 4 to drop recorder-only
* naming. The math is unchanged; only the public type/function names were
* generalised so the framework can serve any geo-anchored consumer.
*
* @see docs/2026-03-08-ref-point-naming-investigation.md §6
*/
/** H3 resolution 11: ~25m edge, ~65m gridDisk safe zone */
const H3_RESOLUTION = 11;
/**
* Compute the H3 resolution-11 index for a GPS position.
*/
function gpsToH3(lat, lng) {
	return latLngToCell(lat, lng, 11);
}
/**
* Approximate distance in metres using equirectangular projection.
* Accurate to < 0.1% for distances under 1 km at mid-latitudes,
* which is sufficient for ranking ref point candidates within a
* gridDisk safe zone (~65–130 m).
*/
function approxDistanceMetres(lat1, lon1, lat2, lon2) {
	const R = 6371e3;
	const dLat = (lat2 - lat1) * Math.PI / 180;
	let dLonDeg = lon2 - lon1;
	dLonDeg -= 360 * Math.floor((dLonDeg + 180) / 360);
	const x = dLonDeg * Math.PI / 180 * Math.cos((lat1 + lat2) / 2 * Math.PI / 180);
	return R * Math.sqrt(dLat * dLat + x * x);
}
/**
* Find the known geo-anchor closest to the given GPS position,
* provided it falls within the gridDisk safe zone.
*
* The safe zone is the center cell plus its 6 neighbors (~65m radius),
* which absorbs GPS jitter of 3-10m while keeping anchors >130m apart
* distinguishable.
*
* When multiple anchors have overlapping safe zones (65–130 m apart),
* the closest one by equirectangular distance is returned.
*/
function findNearbyGeoAnchor(lat, lng, knownAnchors) {
	if (knownAnchors.length === 0) return;
	const safeZone = gridDisk(latLngToCell(lat, lng, 11), 1);
	const candidates = knownAnchors.filter((rp) => safeZone.includes(rp.h3Index));
	if (candidates.length === 0) return void 0;
	if (candidates.length === 1) return candidates[0];
	return candidates.reduce((closest, rp) => {
		return approxDistanceMetres(lat, lng, rp.lat, rp.lon) < approxDistanceMetres(lat, lng, closest.lat, closest.lon) ? rp : closest;
	});
}
/**
* Check whether two H3 indices refer to the same physical location
* (i.e., one falls within the other's gridDisk safe zone).
* This is the canonical cross-session matching check.
*/
function h3CellsMatch(h3a, h3b) {
	if (h3a === h3b) return true;
	return gridDisk(h3a, 1).includes(h3b);
}
/** H3 resolution-11 indices are 15-character lowercase hex strings. */
const H3_INDEX_PATTERN = /^[0-9a-f]{15}$/;
/**
* Check whether a string looks like a valid H3 resolution-11 index.
* Used to distinguish H3 IDs from legacy user-typed string IDs.
*/
function isH3Index(id) {
	return H3_INDEX_PATTERN.test(id);
}
//#endregion
export { H3_RESOLUTION, approxDistanceMetres, findNearbyGeoAnchor, gpsToH3, h3CellsMatch, isH3Index };
